import type { Key } from 'react'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import type { AdvancedQueryPayload } from '../pages/DataVisualization/savedFilterSchemes'

/** 与资产数据列表行结构一致（避免与页面循环依赖） */
export type CachedDataRow = {
  key: string
  id: string
  name: string
  source: string
  type: string
  documentStatus: string
  onChain: string
  contractNo: string
  contractType: string
  customer: string
  settlementOrg: string
  productName: string
  priceTaxTotal: number | null
  receiptAmount: number | null
  partialReceipt: string
  unreceivedAmount: number | null
  invoicedAmount: number | null
  contractCreatedDate: string
  contractReceiptDate: string
  value: number | null
  status: string
  createdTime: string
  ordernumber: string
  bill_no: string
  onChainListed: boolean
  contractAttachment?: string
  orderAttachment?: string
  receiptAttachment?: string
  logisticsAttachment?: string
  invoiceAttachment?: string
}

const CACHE_KEY = 'asset_data_list_state_v1'
const PENDING_RESTORE_KEY = 'asset_data_list_pending_restore_v1'
const MAX_AGE_MS = 30 * 60 * 1000

export type DataListCachePayload = {
  tableData: CachedDataRow[]
  listTotal: number
  listPage: number
  listPageSize: number
  selectedRowKeys: Key[]
  appliedAssetType: string
  appliedAssetStatus: string
  appliedKeyword: string
  appliedSource: string[]
  appliedCustomer: string[]
  appliedSettlementOrg: string[]
  appliedProductName?: string[]
  appliedContractMin: number | null
  appliedContractMax: number | null
  appliedContractCreatedRange: [string | null, string | null] | null
  appliedReceiptDateRange: [string | null, string | null] | null
  appliedDateRange: [string | null, string | null] | null
  appliedAdvancedQuery: AdvancedQueryPayload
  appliedPartialReceipt: 'all' | '是' | '否'
  appliedRwaCategory: string[]
  assetType: string
  assetStatus: string
  tableSort: { columnKey: string; order: 'ascend' | 'descend' }
  summaryStats: {
    totalValue: number
    totalUnreceivedAmount: number
    totalReceiptAmount: number
    totalCount: number
    availableCount: number
  } | null
  ts: number
}

export function saveDataListCache(payload: Omit<DataListCachePayload, 'ts'>): void {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...payload, ts: Date.now() }))
  } catch {
    /* ignore quota */
  }
}

/** 进入详情前标记，返回列表时据此恢复（不依赖 location.state，避免 Strict Mode 丢失） */
export function markListCachePendingRestore(): void {
  try {
    sessionStorage.setItem(PENDING_RESTORE_KEY, '1')
  } catch {
    /* ignore */
  }
}

export function shouldRestoreListCache(): boolean {
  try {
    return sessionStorage.getItem(PENDING_RESTORE_KEY) === '1'
  } catch {
    return false
  }
}

export function clearListCachePendingRestore(): void {
  try {
    sessionStorage.removeItem(PENDING_RESTORE_KEY)
  } catch {
    /* ignore */
  }
}

/** 有待恢复标记时读取列表缓存 */
export function getPendingRestoreCache(): DataListCachePayload | null {
  if (!shouldRestoreListCache()) return null
  return loadDataListCache()
}

export function loadDataListCache(): DataListCachePayload | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as DataListCachePayload
    if (!parsed?.ts || Date.now() - parsed.ts > MAX_AGE_MS) {
      sessionStorage.removeItem(CACHE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export function parseCachedDayRange(t: [string | null, string | null] | null): [Dayjs | null, Dayjs | null] | null {
  if (!t?.[0] || !t?.[1]) return null
  const a = dayjs(t[0])
  const b = dayjs(t[1])
  return a.isValid() && b.isValid() ? [a, b] : null
}
